import { create } from '@bufbuild/protobuf'
import type { NotificationPayload } from '@repo/notifications/use-notifications'
import type { QueryClient } from '@tanstack/react-query'
import { getBalanceQueryKey, BALANCE_QUERY_VARIABLES } from './coin-balance-query'
import { GetBalanceResponseSchema } from '../gen/service/creativefabrica/coins/v2/service_pb'

export type ApplyBalanceUpdatedResult = 'ignored' | 'updated' | 'invalidated'

function toBalance(value: bigint | string | number): bigint {
  if (typeof value === 'bigint') {
    return value
  }
  return BigInt(value)
}

export function applyBalanceUpdatedNotification(
  queryClient: QueryClient,
  notification: NotificationPayload,
  currentUserId: string | undefined,
  workspaceId: string | null = null,
): ApplyBalanceUpdatedResult {
  if (notification.userId && currentUserId && notification.userId !== currentUserId) {
    return 'ignored'
  }

  if (!notification.userId) {
    void queryClient.invalidateQueries({
      queryKey: getBalanceQueryKey(BALANCE_QUERY_VARIABLES, workspaceId),
    })
    return 'invalidated'
  }

  if (!currentUserId) {
    return 'ignored'
  }

  // Balance events identify the user, but do not identify the team wallet.
  if (workspaceId) {
    void queryClient.invalidateQueries({
      queryKey: getBalanceQueryKey(BALANCE_QUERY_VARIABLES, workspaceId),
    })
    return 'invalidated'
  }

  const nextBalance = notification.coins?.balanceUpdated?.balance
  if (nextBalance === undefined || nextBalance === null) {
    void queryClient.invalidateQueries({
      queryKey: getBalanceQueryKey(BALANCE_QUERY_VARIABLES, workspaceId),
    })
    return 'invalidated'
  }

  queryClient.setQueryData(
    getBalanceQueryKey(BALANCE_QUERY_VARIABLES, workspaceId),
    create(GetBalanceResponseSchema, { balance: toBalance(nextBalance) }),
  )
  return 'updated'
}
